import { dirs } from '@scripts/helpers/config';
import * as CONSTANTS from '@scripts/helpers/constants';

/**
 * Generate inline SVG markup for a sprite icon.
 * @param {string} icon
 * @returns {string} SVG markup
 */
export const icon = function (icon) {
	return `
		<svg class="icon icon_${icon}">
			<use href="${dirs.STATIC_ICONS_PATH}#${icon}" />
		</svg>
	`;
};

/**
 * Lazy-initialize a single shared instance.
 * @param {Function} createInstanceFn
 * @returns {{ getInstance: Function }}
 */
export const singletonFactory = function (createInstanceFn) {
	let instance = null;

	return {
		getInstance() {
			if (!instance) {
				instance = createInstanceFn();
			}

			return instance;
		}
	};
};

/**
 * True modulo (always returns positive).
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
export const mod = (a, b) => ((a % b) + b) % b;

/**
 * Measure the browser's scrollbar pixel width.
 * @returns {number}
 */
export const getScrollbarWidth = () => {
	const outer = document.createElement('div');

	Object.assign(outer.style, {
		visibility: 'hidden',
		width: '100px',
		overflow: 'scroll'
	});

	document.body.appendChild(outer);

	const inner = document.createElement('div');
	inner.style.width = '100%';
	outer.appendChild(inner);

	const width = outer.offsetWidth - inner.offsetWidth;

	outer.remove();

	return width;
};

/**
 * Inject a deferred script tag, reusing an existing one if available.
 * @param {string} src
 * @returns {Promise<HTMLScriptElement>}
 */
export const appendScriptToBody = (src) => {
	let curTag = document.querySelector(`script[src*="${src}"]`);

	if (curTag && curTag.dataset.loaded) return Promise.resolve(curTag);

	return new Promise((resolve, reject) => {
		let tag = curTag || document.createElement('script');

		tag.addEventListener('load', () => resolve(tag), {
			once: true
		});
		tag.addEventListener('error', () => reject('Error loading script'), {
			once: true
		});

		if (curTag) return;

		tag.addEventListener(
			'load',
			() => {
				tag.dataset.loaded = true;
			},
			{
				once: true
			}
		);

		tag.setAttribute('src', src);
		tag.setAttribute('defer', 'defer');

		if (!curTag) {
			document.body.append(tag);
		}
	});
};

/**
 * Run a callback after window load, or immediately if already loaded.
 * @param {Function} cb
 */
export const onWindowLoaded = (cb) => {
	if (typeof cb !== 'function') return;

	if (document.readyState === 'complete') return cb(window);

	window.addEventListener('load', cb, {
		once: true
	});
};

/**
 * Run a callback when a CSS transition ends on specific properties.
 * @param {Element} target
 * @param {string|string[]} transitionedProperties
 * @param {Function} cb
 * @param {boolean} [once=true]
 */
export const afterTransition = (target, transitionedProperties, cb, once = true) => {
	const propertiesList = Array.isArray(transitionedProperties)
		? transitionedProperties
		: [transitionedProperties];
	const callback = ({ propertyName }) => {
		if (!propertiesList.includes(propertyName)) return;

		typeof cb === 'function' && cb();

		if (once) {
			target.removeEventListener('transitionend', callback);
		}
	};

	target.addEventListener('transitionend', callback);

	return {
		resolve() {
			typeof cb === 'function' && cb();

			if (once) {
				target.removeEventListener('transitionend', callback);
			}
		},
		destroy() {
			target.removeEventListener('transitionend', callback);
		}
	};
};

/**
 * Get a single DOM element from a selector.
 * @param {*} element
 * @returns {?Element}
 */
export const getElement = (element) => {
	if (Element.prototype.isPrototypeOf(element)) return element;
	if (typeof element === 'string') return document.querySelector(element);
	if (NodeList.prototype.isPrototypeOf(element) && element.length) return element[0];
	if (Array.isArray(element) && element.length) {
		const firstElement = element[0];

		if (Element.prototype.isPrototypeOf(firstElement)) return firstElement;
		if (typeof firstElement === 'string') return document.querySelector(firstElement);
	}
	return null;
};

/**
 * Convert various element types to an array.
 * @param {*} elements
 * @returns {Element[]}
 */
export const getElementsArray = (elements) => {
	if (typeof elements === 'string') return [...document.querySelectorAll(elements)];
	if (NodeList.prototype.isPrototypeOf(elements)) return [...elements];
	if (Element.prototype.isPrototypeOf(elements)) return [elements];

	if (Array.isArray(elements)) {
		const cleanArray = elements
			.map((el) => {
				if (Element.prototype.isPrototypeOf(el)) return el;
				if (typeof el === 'string') return document.querySelector(el);
				return null;
			})
			.filter(Boolean);

		return [...new Set(cleanArray)];
	}

	return [];
};

/**
 * Check if the current build environment is development.
 * @returns {boolean}
 */
export const isDevMode = () => {
	try {
		return process.env.NODE_ENV === 'development';
	} catch {
		return false;
	}
};

/**
 * Lock or unlock page scroll while preserving position.
 * @param {boolean} [lock=true]
 */
export function setHtmlStateLock(lock = true) {
	const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
	const hasPageScrollbar =
		document.documentElement.scrollHeight > document.documentElement.clientHeight;

	if (lock) {
		if (scrollbarWidth > 0 && hasPageScrollbar) {
			document.documentElement.style.scrollbarGutter = 'stable';
		}

		const scrollY = window.scrollY;

		document.body.dataset.scrollY = scrollY.toString();
		// document.body.style.top = `-${scrollY}px`;
		document.documentElement.classList.add(CONSTANTS.LOCKED_CLASS);
	} else {
		if (document.body.dataset.scrollY === undefined) return;

		const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);

		document.documentElement.classList.remove(CONSTANTS.LOCKED_CLASS);
		document.documentElement.style.scrollbarGutter = '';
		document.body.style.top = '';
		delete document.body.dataset.scrollY;

		window.scrollTo({ top: scrollY, behavior: 'instant' });
	}
}


/**
 * Toggles a class on the <html> element.
 * @param {string|null} className
 * @param {boolean} shouldAdd
 */
export function toggleHtmlClass(className, shouldAdd) {
	if (!className) return;
	document.documentElement.classList.toggle(className, shouldAdd);
}

/**
 * Checks if the user prefers reduced motion.
 * @returns {boolean}
 */
export const getMotionPreference = () => {
	const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

	return mediaQuery.matches;
};

/**
 * Checks if the user prefers reduced data usage (Save Data mode).
 * @returns {boolean}
 */
export const getDataPreference = () => {
	if (window.matchMedia('(prefers-reduced-data: reduce)').matches) return true;

	if (navigator.connection && navigator.connection.saveData === true) return true;

	return false;
};

/**
 * Resolves when the window loads.
 * @returns {Promise<void>}
 */
export const onDomReady = () => {
	return new Promise((resolve, reject) => {
		window.addEventListener('load', resolve, { once: true });
	});
};